import { WebhookConfig, WebhookPayload } from '../types';
import { LegacyChainClient } from '../core/LegacyChainClient';
import crypto from 'crypto';

export class WebhookService {
  constructor(private client: LegacyChainClient) {}

  /**
   * Register a new webhook
   */
  async create(config: Omit<WebhookConfig, 'active'>): Promise<WebhookConfig> {
    const response = await this.client.makeRequest<WebhookConfig>(
      'POST',
      '/webhooks',
      {
        ...config,
        active: true
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create webhook');
    }

    return response.data;
  }

  /**
   * List all webhooks
   */
  async list(): Promise<WebhookConfig[]> {
    const response = await this.client.makeRequest<WebhookConfig[]>(
      'GET',
      '/webhooks'
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to list webhooks');
    }

    return response.data;
  }

  /**
   * Get a specific webhook
   */
  async get(webhookId: string): Promise<WebhookConfig> {
    const response = await this.client.makeRequest<WebhookConfig>(
      'GET',
      `/webhooks/${webhookId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get webhook');
    }

    return response.data;
  }

  /**
   * Update webhook configuration
   */
  async update(
    webhookId: string,
    updates: Partial<WebhookConfig>
  ): Promise<WebhookConfig> {
    const response = await this.client.makeRequest<WebhookConfig>(
      'PUT',
      `/webhooks/${webhookId}`,
      updates
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to update webhook');
    }

    return response.data;
  }

  /**
   * Delete a webhook
   */
  async delete(webhookId: string): Promise<boolean> {
    const response = await this.client.makeRequest<{ success: boolean }>(
      'DELETE',
      `/webhooks/${webhookId}`
    );

    return response.success;
  }

  /**
   * Test a webhook by sending a test payload
   */
  async test(webhookId: string): Promise<boolean> {
    const response = await this.client.makeRequest<{ success: boolean }>(
      'POST',
      `/webhooks/${webhookId}/test`
    );

    return response.success;
  }

  /**
   * Verify webhook signature (for receiving webhooks)
   */
  verifySignature(
    payload: string,
    signature: string,
    secret: string
  ): boolean {
    const expectedSignature = crypto
      .createHmac('sha256', secret)
      .update(payload)
      .digest('hex');

    return crypto.timingSafeEqual(
      Buffer.from(signature),
      Buffer.from(expectedSignature)
    );
  }

  /**
   * Get webhook delivery logs
   */
  async getDeliveryLogs(
    webhookId: string,
    options?: {
      limit?: number;
      offset?: number;
      status?: 'success' | 'failed';
    }
  ): Promise<{
    logs: Array<{
      id: string;
      timestamp: number;
      status: 'success' | 'failed';
      statusCode?: number;
      duration: number;
      error?: string;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.status) params.append('status', options.status);

    const response = await this.client.makeRequest<any>(
      'GET',
      `/webhooks/${webhookId}/logs?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get delivery logs');
    }

    return response.data;
  }

  /**
   * Retry a failed webhook delivery
   */
  async retryDelivery(
    webhookId: string,
    deliveryId: string
  ): Promise<boolean> {
    const response = await this.client.makeRequest<{ success: boolean }>(
      'POST',
      `/webhooks/${webhookId}/deliveries/${deliveryId}/retry`
    );

    return response.success;
  }

  /**
   * Available webhook events
   */
  static readonly EVENTS = {
    // Capsule events
    CAPSULE_CREATED: 'capsule.created',
    CAPSULE_UNLOCKED: 'capsule.unlocked',
    CAPSULE_UPDATED: 'capsule.updated',
    CAPSULE_DELETED: 'capsule.deleted',
    
    // Payment events
    PAYMENT_CREATED: 'payment.created',
    PAYMENT_CLAIMED: 'payment.claimed',
    PAYMENT_REFUNDED: 'payment.refunded',
    PAYMENT_EXPIRED: 'payment.expired',
    
    // Document events
    DOCUMENT_CREATED: 'document.created',
    DOCUMENT_SIGNED: 'document.signed',
    DOCUMENT_COMPLETED: 'document.completed',
    DOCUMENT_EXPIRED: 'document.expired',
    DOCUMENT_DESTROYED: 'document.destroyed',
    
    // Account events
    ACCOUNT_UPGRADED: 'account.upgraded',
    ACCOUNT_DOWNGRADED: 'account.downgraded',
    API_KEY_CREATED: 'api_key.created',
    API_KEY_REVOKED: 'api_key.revoked',
    
    // System events
    RATE_LIMIT_EXCEEDED: 'rate_limit.exceeded',
    STORAGE_LIMIT_REACHED: 'storage_limit.reached'
  } as const;
} 